import React, { useEffect, useRef } from 'react';

declare global {
  interface Window {
    Gradient: any;
  }
}

interface AnimatedBackgroundProps {
  darkMode?: boolean;
}

const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  darkMode = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gradientRef = useRef<any>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const initializeGradient = () => {
      if (window.Gradient && canvasRef.current) {
        try {
          // Clean up any existing gradient instance
          if (gradientRef.current) {
            gradientRef.current = null;
          }

          gradientRef.current = new window.Gradient();
          gradientRef.current.initGradient('#gradient-canvas');
          console.log('Gradient initialized successfully');
        } catch (error) {
          console.error('Error initializing gradient:', error);
        }
      }
    };

    // Check if script is already loaded
    if (window.Gradient) {
      initializeGradient();
      return;
    }

    // Check if script is already in DOM to prevent duplicates
    const existingScript = document.querySelector('script[src*="minigl"]');
    if (existingScript) {
      console.log('Minigl script already exists, waiting for load');
      const checkGradient = () => {
        if (window.Gradient && canvasRef.current) {
          initializeGradient();
        } else {
          // Try again in a bit
          timeoutId = setTimeout(checkGradient, 100);
        }
      };
      checkGradient();
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
    }

    // Load the minigl script
    const script = document.createElement('script');
    script.src =
      'https://cdn.jsdelivr.net/gh/greentfrapp/pocoloco@minigl/minigl.js';
    script.async = true;

    script.onload = () => {
      console.log('Minigl script loaded');
      // Wait a bit for the script to fully initialize
      timeoutId = setTimeout(() => {
        if (window.Gradient && canvasRef.current) {
          initializeGradient();
        } else {
          console.error('Gradient class not available or canvas not found');
        }
      }, 100);
    };

    script.onerror = () => {
      console.error('Failed to load minigl script');
    };

    document.head.appendChild(script);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      // Don't remove the script - keep it loaded for future use
      // This prevents errors when toggling the background on/off
    };
  }, []);

  return (
    <div
      className='fixed inset-0 overflow-hidden'
      style={{
        zIndex: 1,
        backgroundColor: darkMode ? '#000000' : '#FFFFFF',
        pointerEvents: 'none',
      }}
    >
      {/* Fallback mesh gradient background */}
      <div className='absolute w-full h-full opacity-55'>
        {/* Multiple overlapping radial gradients to create mesh effect */}
        <div
          className='absolute w-full h-full'
          style={{
            background: `
              radial-gradient(circle at 20% 20%, #2A7B9B 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #57C785 0%, transparent 50%),
              radial-gradient(circle at 20% 80%, #EDDD53 0%, transparent 50%),
              
              radial-gradient(circle at 50% 50%, #2A7B9B 0%, transparent 60%)
            `,
            animation: 'gradient-shift 20s ease infinite',
          }}
        />
        <div
          className='absolute w-full h-full'
          style={{
            background: `
              radial-gradient(circle at 40% 10%, #EDDD53 0%, transparent 40%),
              radial-gradient(circle at 90% 40%, #57C785 0%, transparent 45%),
              
              radial-gradient(circle at 60% 90%, #2A7B9B 0%, transparent 45%),
              radial-gradient(circle at 70% 30%, #57C785 0%, transparent 35%)
            `,
            animation: 'gradient-shift 25s ease infinite reverse',
          }}
        />
        <div
          className='absolute w-full h-full'
          style={{
            background: `
              
              radial-gradient(circle at 85% 15%, #EDDD53 0%, transparent 35%),
              radial-gradient(circle at 35% 85%, #2A7B9B 0%, transparent 40%),
              radial-gradient(circle at 65% 65%, #57C785 0%, transparent 30%)
            `,
            animation: 'gradient-shift 30s ease infinite',
          }}
        />
      </div>

      <canvas
        ref={canvasRef}
        id='gradient-canvas'
        className='absolute w-full h-[60%]'
        style={
          {
            zIndex: 2,
            position: 'absolute',
            transform: 'rotate(0deg) scale(2) translateY(0%)',
            // CSS custom properties for the gradient colors
            '--gradient-color-1': '#2A7B9B',
            '--gradient-color-2': '#57C785',
            '--gradient-color-3': '#EDDD53',
            '--gradient-color-4': '#FFFFFF',
            '--gradient-speed': '0.000002',
          } as React.CSSProperties
        }
      />
    </div>
  );
};

export default AnimatedBackground;
